import { apiFetch } from '@/lib/api/client';
import { CONTENT_API } from '@/lib/api/urls';

import type { AccessScope } from '@/lib/auth';

import type {
  Album,
  Collection,
  ContentItems,
  Game,
  NewsItem,
  Playlist,
  PersonalPlaylist,
  Podcast,
  PodcastChapterStream,
  Quiz,
  Security,
  Video,
  Track,
  TrackFileType,
  Gallery,
} from '@/lib/api/types';

export const fetchAlbum = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Album> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Album>(CONTENT_API.album(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

export const fetchArticle = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<NewsItem> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<NewsItem>(CONTENT_API.article(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

/* ================================================== */
/* ====             COLLECTION                  ===== */
/* ================================================== */
export const fetchCollection = async (args: {
  slugOrId: string | number;
  locale: string;
  universe?: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Collection> => {
  const { slugOrId, locale, universe = 'classical', tenantId } = args;

  return apiFetch<Collection>(
    CONTENT_API.collection(slugOrId, locale, universe),
    { auth: 'none', tenantId, next: { revalidate: 60 } },
  );
};

export const fetchCollectionContents = async (args: {
  collectionId: string | number;
  locale: string;
  universe?: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<ContentItems> => {
  const { collectionId, locale, universe = 'classical', tenantId } = args;

  return apiFetch<ContentItems>(
    CONTENT_API.collectionContents(collectionId, locale, universe),
    { auth: 'none', tenantId, next: { revalidate: 60 } },
  );
};

export const fetchPlaylist = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Playlist> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Playlist>(CONTENT_API.playlist(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

/* ================================================== */
/* ====              PODCASTS                   ===== */
/* ================================================== */
export const fetchPodcast = async (args: {
  slugOrId: number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Podcast> => {
  const { slugOrId, locale, tenantId } = args;

  const url = CONTENT_API.podcast(slugOrId, locale);

  return apiFetch<Podcast>(url, {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

export const fetchPodcastChapterStream = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
}): Promise<PodcastChapterStream> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<PodcastChapterStream>(
    CONTENT_API.podcastChapterStream(slugOrId, locale),
    { auth: 'auto', tenantId },
  );
};

/* ================================================== */
/* ====                VIDEO                    ===== */
/* ================================================== */
export const fetchVideo = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Video> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Video>(CONTENT_API.video(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

export const fetchVideoToken = async (args: {
  id: string | number;
  locale: string;
  headers?: Record<string, string>;
}): Promise<Security> => {
  const { id, locale, headers } = args;

  type BackendTokenResponse = Security | { security?: Security };

  const data = await apiFetch<BackendTokenResponse>(
    `/videos/${encodeURIComponent(String(id))}/token?lang=${encodeURIComponent(locale)}&universeAsFilter=true`,
    { auth: 'none', cache: 'no-store', headers },
  );

  return 'security' in data && data.security
    ? data.security
    : (data as Security);
};

/* ================================================== */
/* ====                QUIZ                     ===== */
/* ================================================== */
export const fetchQuiz = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Quiz> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Quiz>(CONTENT_API.quiz(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

/* ================================================== */
/* ====                GAME                     ===== */
/* ================================================== */
export const fetchGame = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope;
}): Promise<Game> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Game>(CONTENT_API.game(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

/* ================================================== */
/* ====               GALLERY                   ===== */
/* ================================================== */
export const fetchGallery = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope; // optional for consistency; not used by this endpoint today
}): Promise<Gallery> => {
  const { slugOrId, locale, tenantId } = args;

  return apiFetch<Gallery>(CONTENT_API.gallery(slugOrId, locale), {
    auth: 'none',
    tenantId,
    next: { revalidate: 60 },
  });
};

export const fetchPersonalPlaylist = async (args: {
  slugOrId: string | number;
  locale: string;
  tenantId?: string;
  scope?: AccessScope; // optional for consistency; not used by this endpoint today
}): Promise<PersonalPlaylist> => {
  const { slugOrId, locale, tenantId } = args;

  const data = await apiFetch<PersonalPlaylist>(
    CONTENT_API.personalPlaylist(slugOrId, locale),
    {
      auth: 'required',
      tenantId,
    },
  );

  if (!data) {
    throw new Error('Personal playlist not found');
  }

  return {
    ...data,
    playListId: slugOrId,
  };
};

/**
 * Always returns the Track object (incl track_metadata) so UI/normalizers
 * can build credits/composer/album info reliably.
 */
export async function fetchTrackStream(args: {
  siteId: string | number;
  assetId: string | number;
  format: TrackFileType;
  compilationType?:
    | 'personal_favourites'
    | 'personal_recently_viewed'
    | 'album'
    | 'playlist'
    | 'podcast'
    | 'personal_playlist';
  compilationId?: string | number;
  tenantId?: string;
}): Promise<Track> {
  const { siteId, assetId, format, compilationType, compilationId, tenantId } =
    args;

  const qs = new URLSearchParams();
  qs.set('format', format);

  if (
    compilationType &&
    compilationType !== 'personal_favourites' &&
    compilationType !== 'personal_recently_viewed'
  ) {
    qs.set('type', compilationType);
    if (compilationId != null) qs.set('id', String(compilationId));
  }

  const url = `/b2b-sites/${siteId}/tracks/${assetId}?${qs.toString()}`;

  return apiFetch<Track>(url, {
    auth: 'auto',
    tenantId,
  });
}
